import type { VoterDataOrderingDto } from '@signalvote/shared';
import { VoterDataOrderingBlock, assertNever, deliveredCountAllowance } from '@signalvote/shared';

/**
 * What a Voter data order bills, in words: households when the list is one per household,
 * otherwise voters. Shared by the jobs list and the detail page so they always agree.
 */
export function voterDataRecordsLabel(records: number, onePerHousehold: boolean): string {
  const count = records.toLocaleString();
  if (onePerHousehold) return `${count} ${records === 1 ? 'household' : 'households'}, one voter each`;
  return `${count} ${records === 1 ? 'voter' : 'voters'}`;
}

/**
 * Why the delivered file may not match the quote, and the difference past which we contact the
 * customer first, in the list's own records (households or voters).
 */
export function voterDataCountPromise(records: number, onePerHousehold: boolean): string {
  const allowance = deliveredCountAllowance(records);
  const noun = onePerHousehold ? (allowance === 1 ? 'household' : 'households') : allowance === 1 ? 'voter' : 'voters';
  // 5% of a small list rounds down to nothing, so any difference at all is past it.
  const past = allowance === 0 ? 'it differs at all' : `it’s off by more than ${allowance.toLocaleString()} ${noun}`;
  return (
    'The count is live from the voter file, which keeps updating, so your file can differ slightly; you pay the quoted price, ' +
    `and we’ll contact you before delivering if ${past}.`
  );
}

/** Why a Voter data order can't be placed or paid now, in words: the builder and a Draft's order page say the same. */
export function orderingUnavailable(ordering: VoterDataOrderingDto): string {
  switch (ordering.reason) {
    case VoterDataOrderingBlock.PriceNotConfigured:
      return 'Ordering voter data isn’t set up on this site yet.';
    case VoterDataOrderingBlock.PriceMismatch:
      return 'Ordering is paused while this site’s price is corrected.';
    case VoterDataOrderingBlock.WorkOrdersDisabled:
      return 'Ordering voter data isn’t available for this campaign.';
    case VoterDataOrderingBlock.StripeUnavailable:
    case null:
      return 'Ordering is unavailable right now. Please try again shortly.';
    default:
      return assertNever(ordering.reason);
  }
}
